import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { FileLock2, Plus, ShieldCheck, History } from 'lucide-react'
import { evidenceService, EvidenceItem, EvidenceAccess, RegistrationRequest } from '@/services/evidenceService'

export default function EvidencePage() {
    const [items, setItems] = useState<EvidenceItem[]>([])
    const [chain, setChain] = useState<EvidenceAccess[]>([])
    const [sel, setSel] = useState<EvidenceItem | null>(null)
    const [form, setForm] = useState<RegistrationRequest>({ storageKey: '', sha256: '', type: 'PHOTO' })
    const [verifyHash, setVerifyHash] = useState('')
    const [uploadFile, setUploadFile] = useState<File | null>(null)
    const [uploading, setUploading] = useState(false)

    const load = () => { evidenceService.list().then(setItems).catch(() => {}) }
    useEffect(load, [])

    const doUpload = async () => {
        if (!uploadFile) return
        setUploading(true)
        try {
            await evidenceService.upload(uploadFile, form.type || 'PHOTO')
            toast.success('Evidencia subida y registrada')
            setUploadFile(null); load()
        } catch (e: any) {
            toast.error(e?.response?.data?.error || 'Error al subir (¿MinIO configurado?)')
        } finally {
            setUploading(false)
        }
    }

    const register = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!form.storageKey || !form.sha256) return toast.error('storageKey y sha256 obligatorios')
        try { await evidenceService.register(form); toast.success('Evidencia registrada'); setForm({ storageKey: '', sha256: '', type: 'PHOTO' }); load() }
        catch { toast.error('Error al registrar') }
    }

    const select = async (it: EvidenceItem) => {
        setSel(it); setVerifyHash('')
        try { setChain(await evidenceService.chain(it.id)) } catch { setChain([]) }
    }

    const verify = async () => {
        if (!sel) return
        try {
            const r = await evidenceService.verify(sel.id, verifyHash)
            r.matches ? toast.success('Integridad OK: el hash coincide') : toast.error('NO coincide: posible alteración')
        } catch { toast.error('Error al verificar') }
    }

    const inp = 'w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm'
    const card = 'bg-gray-800/40 border border-gray-700 rounded-lg p-4'

    return (
        <div className="p-6 text-gray-100">
            <div className="flex items-center gap-3 mb-6"><FileLock2 className="text-indigo-400" /><h1 className="text-2xl font-bold">Evidencia · Cadena de custodia</h1></div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <form onSubmit={register} className={card + ' space-y-3'}>
                    <h2 className="font-semibold flex items-center gap-2"><Plus size={16} /> Evidencia</h2>

                    {/* Subida real: el archivo va a MinIO y el servidor calcula el hash (E8) */}
                    <div className="pb-3 border-b border-gray-700 space-y-2">
                        <p className="text-xs text-gray-400">Subir archivo (a MinIO + hash automático):</p>
                        <input type="file" onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                            className="text-xs w-full text-gray-300" />
                        <button type="button" disabled={!uploadFile || uploading} onClick={doUpload}
                            className="w-full bg-cyan-700 hover:bg-cyan-600 rounded px-3 py-2 text-sm font-medium disabled:opacity-50">
                            {uploading ? 'Subiendo…' : 'Subir y registrar'}
                        </button>
                    </div>

                    <p className="text-xs text-gray-500">…o registrar manualmente una clave ya existente:</p>
                    <select className={inp} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as any })}>
                        {['PHOTO', 'VIDEO', 'DOC', 'AUDIO', 'OTHER'].map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <input className={inp} placeholder="storageKey (objeto MinIO/S3)" value={form.storageKey} onChange={(e) => setForm({ ...form, storageKey: e.target.value })} />
                    <input className={inp} placeholder="filename" value={form.filename || ''} onChange={(e) => setForm({ ...form, filename: e.target.value })} />
                    <input className={inp} placeholder="SHA-256 (64 hex)" value={form.sha256} onChange={(e) => setForm({ ...form, sha256: e.target.value })} />
                    <input type="number" className={inp} placeholder="Retención (días)" onChange={(e) => setForm({ ...form, retentionDays: parseInt(e.target.value) })} />
                    <button className="w-full bg-indigo-600 hover:bg-indigo-500 rounded px-3 py-2 text-sm font-medium">Registrar</button>
                </form>

                <div className={card}>
                    <h2 className="font-semibold mb-3">Evidencia ({items.length})</h2>
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                        {items.map((it) => (
                            <button key={it.id} onClick={() => select(it)}
                                className={`w-full text-left bg-gray-900/50 hover:bg-gray-900 rounded px-3 py-2 text-sm ${sel?.id === it.id ? 'ring-1 ring-indigo-500' : ''}`}>
                                <div className="font-medium">{it.filename || it.storageKey}</div>
                                <div className="text-gray-500 text-xs font-mono">{it.sha256.slice(0, 24)}…</div>
                            </button>
                        ))}
                        {items.length === 0 && <p className="text-gray-500 text-sm">Sin evidencia.</p>}
                    </div>
                </div>

                <div className={card}>
                    <h2 className="font-semibold mb-3 flex items-center gap-2"><History size={16} /> Cadena de custodia</h2>
                    {!sel && <p className="text-gray-500 text-sm">Selecciona una evidencia.</p>}
                    {sel && (
                        <>
                            <div className="space-y-1 max-h-48 overflow-y-auto mb-3">
                                {chain.map((c) => (
                                    <div key={c.id} className="text-xs bg-gray-900/50 rounded px-2 py-1">
                                        <span className="text-indigo-300">{c.action}</span> · {c.ipAddress || '-'} · {new Date(c.at).toLocaleString()}
                                    </div>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                <input className={inp} placeholder="Hash a verificar" value={verifyHash} onChange={(e) => setVerifyHash(e.target.value)} />
                                <button onClick={verify} className="bg-emerald-600 hover:bg-emerald-500 rounded px-3 text-sm flex items-center gap-1"><ShieldCheck size={14} /></button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}
